import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import cn from 'clsx';
import { Country, getData } from 'country-list';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import { MultiSelectController } from '@components-clean/core/multi-select';
import { TextArea, TextAreaController } from '@components-clean/core/text-area';
import { TextAreaDropdownController } from '@components-clean/core/text-area-dropdown';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { mapCountries, mapNamedItems } from '@pages-clean/risk-assessment/helpers';
import {
    ExtendedCompleteRiskFields,
    ExtendedRiskObservationFields,
} from '@pages-clean/risk-assessment/risk-log/constants';
import styles from '@pages-clean/risk-assessment/risk-log/dialogs/style.module.scss';
import { NamedItemType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import { javascriptDateWithoutTimeFromUTC, stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { notEmpty } from '@utils/utility-helpers';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import {
    getNamedItemsByType,
    getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem,
} from '@queries/generated/common.generated';
import {
    riskObservations_OpraQueries_riskObservations_RiskObservation_classification_NamedItem,
    riskObservations_OpraQueries_riskObservations_RiskObservation_owner_StudyMember,
    riskObservations_OpraQueries_riskObservations_RiskObservation_responsibleFunction_NamedItem,
    riskObservations_OpraQueries_riskObservations_RiskObservation_vendors_NamedItem,
} from '@queries/generated/legacy-risk-assessment.generated';
import { getStudyMembers, getStudyMembersVariables } from '@queries/generated/studies.generated';
import { GET_STUDY_MEMBERS } from '@queries/studies';

interface AddObservationProps {
    studyRef: string;
    onClose: () => void;
    onSubmit: (data: RiskObservationFormInputs) => void;
    observation?: ExtendedRiskObservationFields;
    risk: ExtendedCompleteRiskFields;
}

interface AdditionalInfoProps {
    risk: ExtendedCompleteRiskFields;
}

type ExtendedResponsibleFunction =
    riskObservations_OpraQueries_riskObservations_RiskObservation_responsibleFunction_NamedItem & {
        groupedBy: string;
        disabled: boolean;
    };

type ExtendedClassification = riskObservations_OpraQueries_riskObservations_RiskObservation_classification_NamedItem & {
    groupedBy: string;
    disabled: boolean;
};

export type RiskObservationFormInputs = {
    description: string | null;
    classification: ExtendedClassification | null;
    vendors: riskObservations_OpraQueries_riskObservations_RiskObservation_vendors_NamedItem[] | null;
    countries: Country[] | null;
    responsibleFunction: ExtendedResponsibleFunction | null;
    owner: riskObservations_OpraQueries_riskObservations_RiskObservation_owner_StudyMember | null;
    potentialCause: string | null;
    confirmedCause: string | null;
    observationDate: Date | null;
    closedDate: Date | null;
    resolution: string | null;
    comments: string | null;
};

const COMPONENT_NAME = 'add-observation';

const AdditionalInfo = ({ risk }: AdditionalInfoProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.addObservation' });
    const { t: tCommon } = useTranslation('common');
    return (
        <div className={cn(styles.riskDetails)}>
            <div className="k-d-flex k-gap-12px k-align-items-stretch">
                <div className={cn(styles.label, 'k-font-medium k-flex-shrink-0')}>{t('addInfo.riskId')}</div>
                <div className={cn(styles.content, '')}>{risk.recordId}</div>
            </div>
            <div className="k-d-flex k-gap-12px k-align-items-stretch">
                <div className={cn(styles.label, 'k-font-medium k-flex-shrink-0')}>{t('addInfo.status')}</div>
                <div className={cn(styles.content, '')}>{risk.status}</div>
            </div>
            <div className="k-d-flex k-gap-12px k-align-items-stretch">
                <div className={cn(styles.label, 'k-font-medium k-flex-shrink-0')}>{t('addInfo.owner')}</div>
                <div className={cn(styles.content, styles.wrap, 'k-flex-row k-d-flex k-align-items-center')}>
                    {risk?.owner ? `${risk.owner?.firstName} ${risk.owner?.lastName}` : 'N/A'}
                    {risk?.owner && !risk?.owner?.active && (
                        <div className={cn(styles.inactiveIcon, 'k-pl-sm')}>
                            <InactiveIcon
                                toolTipText={tCommon('common:inactiveIcon')}
                                dataTestId="inactive-icon"
                                tooltipPosition="bottom"
                                className="k-mr-2"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="k-d-flex k-gap-12px k-align-items-stretch">
                <div className={cn(styles.label, 'k-font-medium k-flex-shrink-0')}>
                    {t('addInfo.responsibleFunction')}
                </div>
                <div className={cn(styles.content, styles.wrap, 'k-flex-row k-d-flex k-align-items-center')}>
                    {risk?.responsibleFunction ? risk.responsibleFunction?.name : 'N/A'}
                    {risk?.responsibleFunction && !risk?.responsibleFunction?.active && (
                        <div className={cn(styles.inactiveIcon, 'k-pl-sm')}>
                            <InactiveIcon
                                toolTipText={tCommon('common:inactiveIcon')}
                                dataTestId="inactive-icon"
                                tooltipPosition="bottom"
                                className="k-mr-2"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="k-shrink-0 k-justify-content-left k-d-flex-col k-pb-sm">
                <div className="k-font-medium k-flex-shrink-0 k-pb-sm"> {t('addInfo.statement')}</div>
                <div className="k-overflow-y-auto">
                    <TextArea
                        style={{
                            height: '260px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                        }}
                        name="statement"
                        data-testid="field-select-statement"
                        fillMode="solid"
                        readOnly
                        value={risk.statement ?? ''}
                    />
                </div>
            </div>
        </div>
    );
};

const AddObservationDialog = ({ studyRef, onClose, onSubmit, observation, risk }: AddObservationProps) => {
    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.addObservation' });

    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const [responsibleFunctions, setResponsibleFunctions] = useState<
        ({ groupedBy: string; id: string; name: string; disabled: boolean; active: boolean | null } | null)[]
    >([]);

    const { data: responsibleFunctionData, loading: loadingResponsibleFunctionData } = useQuery<getNamedItemsByType>(
        GET_NAMED_ITEMS_BY_TYPE,
        {
            variables: { type: NamedItemType.RESPONSIBLE_FUNCTION },
        },
    );

    useEffect(() => {
        if (responsibleFunctionData?.namedItemsByType) {
            const studyFunctions = responsibleFunctionData?.namedItemsByType
                .filter(
                    (ni): ni is getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem => ni?.studyId === studyRef,
                )
                .flatMap((studyFunction) => ({
                    groupedBy: studyFunction.active ? 'Active' : 'Inactive',
                    name: studyFunction.name ?? '',
                    id: (studyFunction.id as string) ?? '',
                    disabled: observation?.responsibleFunction
                        ? observation.responsibleFunction.name?.toLowerCase() === studyFunction?.name?.toLowerCase()
                            ? false
                            : !studyFunction.active
                        : !studyFunction.active,
                    active: studyFunction.active,
                    __typename: studyFunction.__typename,
                }))
                .sort((a, b) => {
                    if (a?.active !== b?.active) {
                        return a?.active ? -1 : 1;
                    }

                    if (a.name && b.name) {
                        return a.name?.localeCompare(b.name);
                    }

                    return a?.active ? -1 : 1;
                });
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setResponsibleFunctions(studyFunctions);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [responsibleFunctionData?.namedItemsByType]);

    const [owners, setOwners] = useState<
        ({ fullName: string; id: string; groupedBy: string; active: boolean } | null)[] | undefined
    >([]);

    const [loadOwnerData, { data: ownerData, loading: loadingOwners }] = useLazyQuery<
        getStudyMembers,
        getStudyMembersVariables
    >(GET_STUDY_MEMBERS);

    useEffect(() => {
        loadOwnerData({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    useEffect(() => {
        if (ownerData?.studyMembers) {
            const studyMembers = ownerData.studyMembers
                .filter((user) => user)
                .sort((aOwner, bOwner) => {
                    if (aOwner?.active !== bOwner?.active) {
                        return aOwner?.active ? -1 : 1;
                    }
                    const aName = `${aOwner?.firstName?.toLowerCase() ?? ''} ${aOwner?.lastName?.toLowerCase() ?? ''}`;
                    const bName = `${bOwner?.firstName?.toLowerCase() ?? ''} ${bOwner?.lastName?.toLowerCase() ?? ''}`;
                    return aName.localeCompare(bName);
                })
                .map((user) => ({
                    fullName: `${user?.firstName} ${user?.lastName}`,
                    id: user?.id as string,
                    groupedBy: user?.active ? 'Active' : 'Inactive',
                    disabled: observation?.owner
                        ? observation.owner.firstName?.toLowerCase() === user?.firstName?.toLowerCase() &&
                          observation.owner.lastName?.toLowerCase() === user?.lastName?.toLowerCase()
                            ? false
                            : !user?.active
                        : !user?.active,
                    active: !!user?.active,
                }));
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setOwners(studyMembers);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [ownerData?.studyMembers]);

    const { data: riskObservationClassificationData, loading: loadingRiskObservationClassificationData } =
        useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
            variables: { type: NamedItemType.RISK_OBSERVATION_CLASSIFICATION },
        });

    const { data: riskVendorData, loading: loadingRiskVendorData } = useQuery<getNamedItemsByType>(
        GET_NAMED_ITEMS_BY_TYPE,
        {
            variables: { type: NamedItemType.VENDOR },
        },
    );

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/purity
    const countries = Object.assign(
        [],
        getData().map((countryObject: { code: string; name: string }) => ({
            code: countryObject.code,
            name: countryObject.name,
        })),
    );

    const {
        control,
        handleSubmit,
        reset,
        getValues,
        formState: { isValid, isDirty, isSubmitting, dirtyFields },
    } = useForm<RiskObservationFormInputs>({
        mode: 'all',
        defaultValues: {
            description: observation?.description ?? null,
            classification: observation?.classification
                ? {
                      ...observation.classification,
                      groupedBy: observation.classification.active ? 'Active' : 'Inactive',
                      disabled: !observation.classification.active,
                  }
                : null,
            vendors:
                observation?.vendors?.map((riskVendor) => ({
                    id: riskVendor?.id,
                    name: riskVendor?.name,
                    groupedBy: riskVendor?.active ? 'Active' : 'Inactive',
                    disabled: false,
                    active: riskVendor?.active,
                    __typename: riskVendor?.__typename,
                })) ?? [],
            countries:
                observation?.countries?.filter(notEmpty).map((country) => ({
                    code: country?.code ?? '',
                    name: country?.name ?? '',
                })) ?? [],
            responsibleFunction: observation?.responsibleFunction
                ? {
                      ...observation.responsibleFunction,
                      groupedBy: observation.responsibleFunction.active ? 'Active' : 'Inactive',
                      disabled: !observation.responsibleFunction.active,
                  }
                : null,
            owner: observation
                ? (owners?.find((ownerObject) => ownerObject?.id === observation?.ownerId) ?? null)
                : null,
            potentialCause: observation?.potentialCause ?? '',
            confirmedCause: observation?.confirmedCause ?? '',
            observationDate: observation?.observationDate
                ? javascriptDateWithoutTimeFromUTC(observation.observationDate)
                : null,
            closedDate: observation?.closedDate ? javascriptDateWithoutTimeFromUTC(observation.closedDate) : null,
            resolution: observation?.resolution ?? '',
            comments: observation?.comments ?? '',
        },
    });

    const createdBy = `${stringDateWithoutTimeFromUtc(observation?.created, 'DD-MMM-YYYY')} by ${observation?.createdBy}`;
    const updatedBy = `${stringDateWithoutTimeFromUtc(observation?.updated, 'DD-MMM-YYYY')} by ${observation?.updatedBy}`;

    useEffect(() => {
        if (observation && owners && owners.length > 0) {
            reset({
                ...getValues(),
                owner: owners.find((ownerObject) => ownerObject?.id === observation.ownerId) ?? null,
            });
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [owners]);

    const dialogTitle = observation
        ? `${tRoot('common:edit')} ${t('title')}: ${observation.recordId}`
        : `${tRoot('common:add')} ${t('title')}`;
    const primaryBtnLabel = observation ? tRoot('common:update') : tRoot('common:add');

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isDirty]);

    const onCloseHandler = async () => {
        if (componentHasChanges(COMPONENT_NAME) && Object.keys(dirtyFields).length > 0) {
            const confirmed = await confirmDiscardChanges('add-risk-unsaved-dialog');
            if (!confirmed) {
                return;
            }
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        onClose();
    };
    return (
        <DraggableDialog
            className={cn(styles.kWindowContent)}
            dataTestId="add-observation-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmit)()}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            initialHeight={observation ? 758 : 683}
            initialWidth={542}
            createdUpdatedInfo={
                observation ? <CreatedUpdatedInfo created={createdBy} updated={updatedBy} /> : undefined
            }
            createdUpdatedBackgroundColour="gray"
            bodyPadding="none"
            additionalInfoWidth={350}
            additionalInfoBtnLabel={t('addInfo.buttonText')}
            additionalInfo={<AdditionalInfo risk={risk} />}
        >
            <div className={cn(styles.editObservationDialog, styles.rightBorderStyle, 'k-w-full')}>
                {observation && (
                    <div className={cn(styles.metaData, 'k-py-sm')}>
                        <div className={cn(styles.divider, 'k-shrink-0 k-d-flex')}>
                            <div className="k-flex k-justify-content-center k-flex-row k-d-flex k-align-items-center k-gap-2">
                                <div className="k-font-medium">{t('metaDataLabels.status')}</div>
                                <div className={cn(styles.editSiteObservationTopInfoLabel)}>{observation.status}</div>
                            </div>
                            <div className="k-flex k-justify-content-center k-flex-row k-d-flex k-align-items-center k-gap-2">
                                <div className="k-font-medium">{t('metaDataLabels.numOpenActions')}</div>
                                <div className={cn(styles.editSiteObservationTopInfoLabel)}>
                                    {observation.numOpenActions} / {observation.numActions}
                                </div>
                            </div>
                            <div className="k-flex k-justify-content-center k-flex-row k-d-flex k-align-items-center k-gap-2">
                                <div className="k-font-medium">{t('metaDataLabels.recentAction')}</div>
                                <div className={cn(styles.editSiteObservationTopInfoLabel)}>
                                    {stringDateWithoutTimeFromUtc(observation.recentActionDate, 'DD-MMM-YYYY') ?? 'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <form id="add-observation" className="k-w-full k-pl-3.5 k-pr-3.5">
                    <div className="k-d-flex k-flex-auto k-pt-sm k-justify-content-center">
                        <TextAreaController
                            className={styles.textAreaOverrides}
                            maxLength={3000}
                            control={control}
                            name="description"
                            data-testid="observation-field-observation"
                            label={t('fields.observation')}
                            required
                        />
                    </div>
                    <div className={cn('k-shrink-0 k-d-flex k-pb-sm', styles.formFieldGap)}>
                        <div className={cn('!k-basis-1/2', styles.minWidthFix, styles.dropdownFix)}>
                            <BasicDropDownController
                                control={control}
                                name="classification"
                                data-testid="risk-field-control-strategy"
                                data={
                                    riskObservationClassificationData?.namedItemsByType
                                        ? mapNamedItems(
                                              riskObservationClassificationData.namedItemsByType.filter(
                                                  (i) => i !== null,
                                              ),
                                              observation?.classification?.name
                                                  ? [observation?.classification.name]
                                                  : undefined,
                                          )
                                        : []
                                }
                                disabled={loadingRiskObservationClassificationData}
                                loading={loadingRiskObservationClassificationData}
                                textField="name"
                                groupField="groupedBy"
                                activeField="active"
                                label={t('fields.classification')}
                                dataItemKey="id"
                                filterable
                                allowClear
                            />
                        </div>
                    </div>
                    <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-sm">
                        <MultiSelectController
                            isEditing
                            control={control}
                            name="vendors"
                            data={
                                Array.isArray(riskVendorData?.namedItemsByType)
                                    ? mapNamedItems(
                                          riskVendorData.namedItemsByType,
                                          observation?.vendors?.map((vendor) => vendor?.name).filter(notEmpty),
                                      )
                                    : []
                            }
                            loading={loadingRiskVendorData}
                            disabled={loadingRiskVendorData}
                            data-testid="risk-field-vendors"
                            label={t('fields.vendors')}
                            textField="name"
                            dataItemKey="id"
                            groupField="groupedBy"
                            activeField="active"
                            filterable
                            allowCustom={false}
                        />
                    </div>
                    <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-sm">
                        <MultiSelectController
                            isEditing
                            control={control}
                            name="countries"
                            data={mapCountries(countries, (observation?.countries ?? []) as Country[])}
                            data-testid="risk-field-countries"
                            label={t('fields.countries')}
                            textField="name"
                            dataItemKey="code"
                            filterable
                            allowCustom={false}
                            isCountryMultiSelect
                        />
                    </div>
                    <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-md', styles.formFieldGap)}>
                        <div className={cn('!k-basis-1/2', styles.minWidthFix, styles.dropdownFix)}>
                            <BasicDropDownController
                                control={control}
                                name="owner"
                                data-testid="risk-field-owner"
                                data={owners ?? []}
                                loading={loadingOwners}
                                textField="fullName"
                                groupField="groupedBy"
                                activeField="active"
                                label={t('fields.owner')}
                                dataItemKey="id"
                                filterable
                                allowClear
                            />
                        </div>
                        <div className={cn('!k-basis-1/2', styles.minWidthFix, styles.dropdownFix)}>
                            <BasicDropDownController
                                control={control}
                                name="responsibleFunction"
                                data-testid="observation-field-responsible-function"
                                data={responsibleFunctions}
                                disabled={loadingResponsibleFunctionData}
                                loading={loadingResponsibleFunctionData}
                                textField="name"
                                groupField="groupedBy"
                                activeField="active"
                                label={t('fields.responsibleFunction')}
                                dataItemKey="id"
                                filterable
                                allowClear
                            />
                        </div>
                    </div>
                    <div className="k-pb-md k-input-md">
                        <TextAreaDropdownController
                            maxLength={3000}
                            className={styles.textAreaOverrides}
                            control={control}
                            name="potentialCause"
                            data-testid="field-select-potentialCause"
                            label={t('fields.potentialCause')}
                            rows={4}
                        />
                    </div>
                    <div className="k-pb-sm k-input-md">
                        <TextAreaDropdownController
                            maxLength={3000}
                            className={styles.textAreaOverrides}
                            control={control}
                            name="confirmedCause"
                            data-testid="field-select-confirmedCause"
                            label={t('fields.confirmedCause')}
                            rows={4}
                        />
                    </div>
                    <div className={cn('k-shrink-0 k-d-flex k-justify-content-left k-pb-md', styles.formFieldGap)}>
                        <div className={cn('!k-basis-1/2', styles.minWidthFix)}>
                            <DatePickerController
                                control={control}
                                name="observationDate"
                                data-testid="datepicker-observationDate"
                                label={t('fields.observationDate')}
                                fillMode="solid"
                            />
                        </div>
                        <div className={cn('!k-basis-1/2', styles.minWidthFix)}>
                            <DatePickerController
                                control={control}
                                name="closedDate"
                                data-testid="datepicker-closedDate"
                                label={t('fields.closedDate')}
                                fillMode="solid"
                                readonly={
                                    observation?.actions?.some(
                                        (action) => action?.completedDate === null && !action?.deleted,
                                    ) && observation?.closedDate === null
                                }
                            />
                        </div>
                    </div>
                    <div className="k-pb-md k-input-md">
                        <TextAreaDropdownController
                            maxLength={3000}
                            className={styles.textAreaOverrides}
                            control={control}
                            name="resolution"
                            data-testid="field-select-resolution"
                            label={t('fields.resolution')}
                            rows={4}
                        />
                    </div>
                    <div className="k-pb-md k-input-md">
                        <TextAreaDropdownController
                            maxLength={3000}
                            className={styles.textAreaOverrides}
                            control={control}
                            name="comments"
                            data-testid="field-select-comments"
                            label={t('fields.comments')}
                            rows={4}
                        />
                    </div>
                </form>
            </div>
        </DraggableDialog>
    );
};

export default AddObservationDialog;
